import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@platform/ui'
import { Lock } from 'lucide-react'
import { useTranslations } from '@/i18n/useTranslations'
import { startUnblock, answerUnblock, type SecurityQuestionView } from '@/lib/account-security/api'
import { MaskedInput } from '@/modules/account-security/MaskedInput'

type Step = 'email' | 'questions' | 'done'
type Msg = { type: 'info' | 'error' | 'success'; text: string } | null

/**
 * Pantalla "cuenta bloqueada → solicitar desbloqueo" (sin sesión). El usuario
 * ingresa su email, responde sus 3 preguntas de seguridad (máx. 4 intentos) y la
 * solicitud queda PENDING para que un admin la apruebe. Backend: /unblock/*.
 */
export function UnblockPage() {
  const t = useTranslations('SecurityQuestions')
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [requestId, setRequestId] = useState<string | null>(null)
  const [questions, setQuestions] = useState<SecurityQuestionView[]>([])
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [msg, setMsg] = useState<Msg>(null)
  const [loading, setLoading] = useState(false)

  async function start(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setMsg(null)
    try {
      const r = await startUnblock(email)
      if (!r.requestId || r.questions.length === 0) {
        setMsg({ type: 'info', text: t('unblock_generic') })
        return
      }
      setRequestId(r.requestId)
      setQuestions(r.questions)
      setStep('questions')
    } catch {
      setMsg({ type: 'error', text: t('error_generic') })
    } finally {
      setLoading(false)
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!requestId) return
    setLoading(true)
    setMsg(null)
    try {
      const payload = questions.map((q) => ({ position: q.position, answer: answers[q.position] ?? '' }))
      const r = await answerUnblock(requestId, payload)
      if (r.status === 'APPROVED' || (r as any).unlocked) {
        // Grado 1: la cuenta se reactivó sola.
        setStep('done')
        setMsg({ type: 'success', text: t('unblock_unlocked') })
      } else if (r.status === 'PENDING') {
        // Grado 2: acertó, queda a la espera de aprobación del admin.
        setStep('done')
        setMsg({ type: 'success', text: t('unblock_pending') })
      } else if (r.status === 'LOCKED') {
        setStep('done')
        // Escaló de grado 1 a grado 2 por agotar los intentos.
        setMsg({ type: 'error', text: t((r as any).escalated ? 'unblock_escalated' : 'unblock_locked') })
      } else {
        setMsg({ type: 'error', text: t('unblock_wrong', { left: r.attemptsLeft }) })
      }
    } catch {
      setMsg({ type: 'error', text: t('error_generic') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" />
            {t('unblock_title')}
          </CardTitle>
          <CardDescription>{t('unblock_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {msg && (
            <Alert variant={msg.type === 'error' ? 'destructive' : 'default'}>
              <AlertDescription role={msg.type === 'error' ? 'alert' : 'status'}>{msg.text}</AlertDescription>
            </Alert>
          )}

          {step === 'email' && (
            <form onSubmit={start} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t('unblock_email')}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full">
                {t('unblock_start')}
              </Button>
            </form>
          )}

          {step === 'questions' && (
            <form onSubmit={submit} className="space-y-4">
              {questions.map((q) => (
                <div key={q.position} className="space-y-1.5">
                  <Label htmlFor={`q${q.position}`}>{t(`q.${q.question}`)}</Label>
                  <MaskedInput
                    id={`q${q.position}`}
                    required
                    ariaLabel={t(`q.${q.question}`)}
                    value={answers[q.position] ?? ''}
                    onChange={(v) => setAnswers((a) => ({ ...a, [q.position]: v }))}
                  />
                </div>
              ))}
              <Button type="submit" disabled={loading} className="w-full">
                {t('unblock_submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/unblock')({ component: UnblockPage })
